import type { DragEvent, MouseEvent } from 'react';
import type { TicketListItem } from '../../api/types';
import { formatPoints } from '../../lib/format';
import { secondLineParts } from '../../lib/second-line';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { Avatar } from '../ui/Avatar';
import { PriorityIcon } from './PriorityIcon';
import { StatusDropdown } from './StatusDropdown';
import { TypeIcon } from './TypeIcon';

/**
 * 🔴 THE ROW. Copied from Or's screenshots 1–4, which are the prototype.
 *
 *   line 1  [type icon] APFS-441  Summary …            [STATUS ▾] [10] [⌃⌃] [avatar]
 *   line 2              Significant / Large • v1.0.33, v1.0.34 • AI, MCP
 *
 * ⭐ The second line is the whole point: it is how 26 items are scanned without opening one.
 *
 * 🔴 MOBILE IS A RE-THINK, NOT A REFLOW. The desktop row is one line with four right-aligned
 * controls; at 390px that becomes an unreadable squeeze or a horizontal scrollbar. The same elements
 * are re-laid-out by the grid below — the summary takes the full width and the controls drop to
 * their own strip where the status chip is a real tap target. No horizontal scrolling anywhere.
 */
/**
 * The expand control, present only when the row is a parent WITH children — see `ticket-tree.ts`.
 * `countIsFloor` renders "3+" rather than "3": a count derived from the current page cannot rule out
 * a fourth child on page 2, and a confident wrong number is worse than an obviously partial one.
 */
export interface RowExpansion {
  expanded: boolean;
  childCount: number | null;
  countIsFloor: boolean;
  childrenIncomplete: boolean;
  onToggle: () => void;
}

export interface RowNesting {
  /** 1 for a sub-task drawn under its parent. */
  depth: 0 | 1;
  /** The parent's key, for the child row's accessible name. */
  parentKey?: string | null;
  /**
   * ⚠️ A sub-task whose parent is NOT on this page. It is drawn at the TOP level — the alternative is
   * dropping it between pages — and this badge is what stops it reading as an ordinary ticket.
   */
  detached?: boolean;
  onOpenParent?: () => void;
}

/**
 * Backlog drag-to-reorder, per row. Absent on every screen that is not the sprint backlog.
 *
 * 🔴 THE BUTTONS ARE NOT A NICETY — THEY ARE THE ONLY PATH ON HALF THE DEVICES. HTML5 drag-and-drop
 * does not fire on touch at all: `dragstart` has no touch equivalent, so a phone gets NO reorder from
 * the handle no matter how it is styled. ⛔ Shipping the handle alone would be a feature that is
 * invisible on the layout Or reviews on, and invisible to a keyboard too. ⭐ Same pair the vocabulary
 * editor already ships (`features/settings/VocabRowItem.tsx`): a handle for the mouse, Move up/down
 * for everything else, both calling the same reorder.
 */
export interface RowReorder {
  dragging: boolean;
  /** This row is the one a drop would land on. */
  dropTarget: boolean;
  /** A reorder is in flight — the controls are disabled rather than queueing a second write. */
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
  onMove: (delta: -1 | 1) => void;
}

export function TicketRow({
  ticket,
  selected,
  onSelect,
  onStatusChanged,
  expansion,
  nesting,
  reorder,
}: {
  ticket: TicketListItem;
  selected: boolean;
  onSelect: (ticket: TicketListItem) => void;
  onStatusChanged: () => void;
  expansion?: RowExpansion;
  nesting?: RowNesting;
  reorder?: RowReorder;
}) {
  const vocab = useVocabulary();
  const isChild = nesting?.depth === 1;
  const type = vocab.type(ticket.type);
  const priority = vocab.priority(ticket.priority);
  const impact = vocab.impact(ticket.impact);
  const points = formatPoints(ticket.story_points);
  const isClosed = vocab.statuses.find((entry) => entry.code === ticket.status)?.is_final ?? false;

  // Clicking the row selects it — except on a nested control, which owns its own click.
  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-row-control]')) return;
    onSelect(ticket);
  };

  return (
    <div
      onClick={handleRowClick}
      data-selected={selected || undefined}
      data-depth={nesting?.depth ?? 0}
      data-dragging={reorder?.dragging || undefined}
      data-drop-target={reorder?.dropTarget || undefined}
      // ⚠️ The whole row is the drop TARGET but only the handle starts a drag — `draggable` on the
      //    row would make every text selection inside it start one instead.
      onDragOver={reorder?.onDragOver}
      onDragLeave={reorder?.onDragLeave}
      onDrop={reorder?.onDrop}
      className={`grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2.5 gap-y-1 border-b border-l-2 border-b-[color:var(--color-line)] px-3 py-2.5 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-y-0.5 sm:px-4 sm:py-2 ${
        selected
          ? 'border-l-[color:var(--color-accent)] bg-sky-50 dark:bg-sky-950/40'
          : 'border-l-transparent hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'
      } ${isChild && !selected ? 'bg-black/[0.02] dark:bg-white/[0.025]' : ''} ${
        reorder?.dragging ? 'opacity-40' : ''
      } ${
        // ⭐ A LINE, not a background tint: the drop lands BETWEEN rows, and a filled row says
        //   "into this one", which is not what will happen.
        reorder?.dropTarget ? 'border-t-2 border-t-[color:var(--color-accent)]' : ''
      }`}
    >
      {/* ⭐ The indent, the chevron and the type icon share column 1, so the two-line layout below is
          untouched at both depths and every row's summary still starts in the same place. */}
      <span className="col-start-1 row-start-1 flex items-start gap-1">
        {reorder ? <ReorderControls reorder={reorder} ticketKey={ticket.key} /> : null}
        {isChild ? (
          <span
            aria-hidden="true"
            className="ml-1 h-4 w-4 shrink-0 self-stretch border-l border-[color:var(--color-line)] sm:ml-2 sm:w-5"
          />
        ) : null}
        <ExpandToggle expansion={expansion} ticketKey={ticket.key} />
        <span className="mt-[3px]">
          <TypeIcon type={type} />
        </span>
      </span>

      {/* line 1 — key + summary */}
      <div className="col-start-2 row-start-1 flex min-w-0 flex-wrap items-baseline gap-x-2">
        {isChild && nesting?.parentKey ? (
          <span className="sr-only">Sub-task of {nesting.parentKey}. </span>
        ) : null}
        <button
          type="button"
          onClick={() => onSelect(ticket)}
          className="shrink-0 text-left font-mono text-[13px] font-semibold text-[color:var(--color-accent)] hover:underline"
        >
          {/* ⭐ `key` is derived in SQL (project.key || '-' || number) and always present, so the
              client never assembles it and can never disagree with the server about a ticket's name. */}
          <span className={isClosed ? 'line-through decoration-2' : undefined}>{ticket.key}</span>
        </button>
        <span
          className="line-clamp-2 min-w-0 basis-full text-[13px] leading-snug text-[color:var(--color-ink)] sm:line-clamp-1 sm:basis-0 sm:grow"
          title={ticket.title}
        >
          {ticket.title}
        </span>
        {expansion ? <SubtaskCount expansion={expansion} /> : null}
        {nesting?.detached ? <DetachedBadge onOpenParent={nesting.onOpenParent} /> : null}
      </div>

      {/* line 2 — [sprint] Impact • versions • modules. ⛔ ONE LINE, ALWAYS: `flex` does not wrap. */}
      <SecondLine ticket={ticket} impactLabel={impact?.label ?? null} />

      {/* controls — right cluster on desktop, own strip on mobile */}
      <div
        data-row-control
        className="col-start-2 row-start-3 flex items-center gap-2 sm:col-start-3 sm:row-span-2 sm:row-start-1 sm:self-center"
      >
        <StatusDropdown ticketId={ticket.id} statusCode={ticket.status} onChanged={onStatusChanged} />
        <span
          className="inline-flex h-5 min-w-6 shrink-0 items-center justify-center rounded bg-[color:var(--color-surface-sunken)] px-1 text-[11px] font-semibold text-[color:var(--color-ink-muted)]"
          title={points ? `${points} story points` : 'No estimate'}
        >
          {points ?? '–'}
        </span>
        <PriorityIcon priority={priority} />
        {/* SafeUser has no avatar URL and the ticket carries only a handle, so this is initials. */}
        <Avatar name={ticket.assignee} title={ticket.assignee ? `Assignee: ${ticket.assignee}` : 'Unassigned'} />
      </div>
    </div>
  );
}

/**
 * The chevron, in a slot that is the SAME WIDTH when there is no chevron.
 *
 * ⭐ A row with no sub-tasks keeps the empty slot rather than shifting its type icon left: 50 rows
 * whose icons do not line up is the visual noise that makes a dense list unreadable.
 * ⛔ And the slot is empty, not a disabled chevron — a control that cannot do anything still invites
 * a click, and "this parent has no sub-tasks" is exactly what its absence says.
 */
/**
 * The reorder affordance: a drag handle for pointers, Move up/down for everything else.
 *
 * ⛔ `data-row-control` on the wrapper — without it, `handleRowClick` treats a click on any of these
 *   as a click on the row and opens the detail panel underneath the gesture.
 *
 * ⚠️ THE HANDLE IS `hidden sm:flex` AND THE BUTTONS ARE ALWAYS PRESENT. That is not a tidy-up: at
 *   390px there is no drag to offer, because touch does not produce drag events — so the phone gets
 *   the buttons, which work there and work for a keyboard at every width.
 */
function ReorderControls({ reorder, ticketKey }: { reorder: RowReorder; ticketKey: string }) {
  return (
    <span data-row-control className="mt-[1px] flex shrink-0 items-center">
      <span
        draggable={!reorder.busy}
        onDragStart={reorder.onDragStart}
        onDragEnd={reorder.onDragEnd}
        aria-hidden="true"
        title={`Drag to reorder ${ticketKey} within this section`}
        className={`hidden h-[18px] w-3 shrink-0 cursor-grab items-center justify-center text-[color:var(--color-ink-subtle)] active:cursor-grabbing sm:flex ${
          reorder.busy ? 'pointer-events-none opacity-40' : ''
        }`}
      >
        {/* Six dots — the conventional grab handle. */}
        <svg viewBox="0 0 6 16" width="6" height="14" fill="currentColor" focusable="false">
          <circle cx="1.5" cy="4" r="1" />
          <circle cx="4.5" cy="4" r="1" />
          <circle cx="1.5" cy="8" r="1" />
          <circle cx="4.5" cy="8" r="1" />
          <circle cx="1.5" cy="12" r="1" />
          <circle cx="4.5" cy="12" r="1" />
        </svg>
      </span>

      <span className="flex flex-col">
        <MoveButton
          direction="up"
          ticketKey={ticketKey}
          disabled={reorder.busy || !reorder.canMoveUp}
          onClick={() => reorder.onMove(-1)}
        />
        <MoveButton
          direction="down"
          ticketKey={ticketKey}
          disabled={reorder.busy || !reorder.canMoveDown}
          onClick={() => reorder.onMove(1)}
        />
      </span>
    </span>
  );
}

function MoveButton({
  direction,
  ticketKey,
  disabled,
  onClick,
}: {
  direction: 'up' | 'down';
  ticketKey: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      // ⭐ The ticket's key is IN the label. "Move up" repeated down a list of fifty rows tells a
      //   screen-reader user nothing about which row they are on.
      aria-label={`Move ${ticketKey} ${direction} in this section`}
      className="flex h-[11px] w-4 items-center justify-center text-[9px] leading-none text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink)] disabled:opacity-25 disabled:hover:text-[color:var(--color-ink-subtle)]"
    >
      <span aria-hidden="true">{direction === 'up' ? '▲' : '▼'}</span>
    </button>
  );
}

function ExpandToggle({
  expansion,
  ticketKey,
}: {
  expansion: RowExpansion | undefined;
  ticketKey: string;
}) {
  if (!expansion) return <span aria-hidden="true" className="w-4 shrink-0 sm:w-[18px]" />;

  const count =
    expansion.childCount === null
      ? 'sub-tasks'
      : `${expansion.childCount}${expansion.countIsFloor ? ' or more' : ''} sub-task${
          expansion.childCount === 1 && !expansion.countIsFloor ? '' : 's'
        }`;

  return (
    <span data-row-control>
      <button
        type="button"
        onClick={expansion.onToggle}
        aria-expanded={expansion.expanded}
        aria-label={`${expansion.expanded ? 'Hide' : 'Show'} the ${count} of ${ticketKey}`}
        className="mt-[1px] flex h-4 w-4 shrink-0 items-center justify-center rounded text-[color:var(--color-ink-muted)] hover:bg-black/10 sm:h-[18px] sm:w-[18px] dark:hover:bg-white/15"
      >
        <svg
          viewBox="0 0 16 16"
          width="12"
          height="12"
          aria-hidden="true"
          className={`transition-transform duration-100 ${expansion.expanded ? 'rotate-90' : ''}`}
        >
          <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
    </span>
  );
}

/**
 * `3 sub-tasks` — Jira's `(18 subtasks)` from screenshot 1.
 *
 * ⚠️ `3+` when the number came from the current page alone. The two are different claims and the row
 * is not allowed to make the stronger one on the weaker evidence.
 */
function SubtaskCount({ expansion }: { expansion: RowExpansion }) {
  const { childCount, countIsFloor, childrenIncomplete } = expansion;
  if (childCount === null) return null;

  return (
    <span
      className="shrink-0 rounded bg-[color:var(--color-surface-sunken)] px-1.5 text-[11px] font-medium text-[color:var(--color-ink-muted)]"
      title={
        countIsFloor
          ? 'At least this many sub-tasks — counted from this page only, so there may be more'
          : childrenIncomplete
            ? 'Only the first 100 sub-tasks can be listed'
            : `${childCount} sub-tasks`
      }
    >
      {childCount}
      {countIsFloor ? '+' : ''} sub-task{childCount === 1 && !countIsFloor ? '' : 's'}
    </span>
  );
}

/**
 * 🔴 The badge on a sub-task whose parent is NOT on this page.
 *
 * The list is one flat `updated_at DESC` window with no way to ask for top-level rows only, so a
 * child can land on a page its parent is not on. ⛔ Hiding it there would make it invisible — it is
 * not under a parent on this page and it is not a page of its own. It is shown, and labelled, and
 * the label opens the parent.
 */
function DetachedBadge({ onOpenParent }: { onOpenParent?: () => void }) {
  return (
    <span
      data-row-control
      className="shrink-0 rounded border border-[color:var(--color-line)] px-1.5 text-[11px] text-[color:var(--color-ink-muted)]"
      title="A sub-task. Its parent is not on this page, so it is listed here rather than hidden."
    >
      ↳ sub-task{' '}
      {onOpenParent ? (
        <button
          type="button"
          onClick={onOpenParent}
          className="font-medium text-[color:var(--color-accent)] hover:underline"
        >
          open parent
        </button>
      ) : null}
    </span>
  );
}

/**
 * 🔴 THE SPRINT, AND WHY IT IS A CHIP RATHER THAN A FOURTH DOT-SEPARATED WORD.
 *
 * ⭐⭐ WHAT JIRA ACTUALLY DOES — screenshots 1 and 2 — IS NOT PUT THE SPRINT ON THE ROW AT ALL. It is
 *   the GROUP HEADER (`APFS v1.0.33  14 Jul – 14 Aug (26 work items)`) and every row beneath it
 *   inherits it. Sprint is a CONTAINER there, not a row attribute. ⛔ That option is closed to us:
 *   `GET /tickets` is one flat `updated_at DESC` window with no grouping, so there is no header for
 *   a sprint to live in and the rows in view can belong to several.
 *   ⇒ Screenshot 3 is the fallback Jira itself uses when it must show the sprint ON an item: in the
 *     detail panel `Sprint` is a BLUE LINK, while Module and Fix Version are grey chips and Impact
 *     is grey text. Jira marks it as a DIFFERENT KIND OF FACT — somewhere you go, not something the
 *     ticket is. This chip is that distinction, carried onto the row.
 *
 * 🔴 AND THE LAYOUT RULE THAT MATTERS AT 390px. Appending a fourth item to
 *   `Impact • versions • modules` would wrap on a phone, and a second line that wraps stops being
 *   scannable — which is the only reason the line exists. So it does not append:
 *     · the chip is `shrink-0` and FIRST, so the sprint is never the thing that gets cut;
 *     · the rest keeps `truncate` and absorbs the loss with an ellipsis;
 *     · `flex` (nowrap) makes the one-line guarantee structural rather than a hope about lengths.
 *   ⭐ Truncation over hiding, deliberately: an ellipsis SHOWS that something was cut, and dropping
 *     modules below some width would silently make "has no modules" and "too narrow" identical —
 *     the exact conflation `SecondLine` already refuses for `undefined` vs `[]`.
 *   ⭐ And it is what Jira does at a narrow width: screenshot 2 truncates the TITLE and leaves the
 *     second line whole. The structured facts outrank the prose.
 *
 * ⛔ NOT A BUTTON, though it is tempting — clicking a sprint to filter by it is the obvious gesture.
 *   An ~80px chip is well under a 44px-high tap target inside a row that is ITSELF tappable, so on
 *   the 390px screen where Or is reading this it would mostly produce mis-taps into the wrong
 *   ticket. The filter is a real control in the bar; this stays a label.
 *
 * ⚠️ Absent and null render identically — no chip. See `sprint_name` in types.ts for why that
 *   conflation is correct HERE and wrong for `modules`.
 */
function SecondLine({ ticket, impactLabel }: { ticket: TicketListItem; impactLabel: string | null }) {
  // ⭐ The CONTENT decision lives in `second-line.ts` and is unit-tested there. This function is only
  //   the layout — which is exactly the split that makes the no-wrap rule assertable.
  const { sprint, text } = secondLineParts(ticket, impactLabel);
  if (!sprint && !text) return null;

  return (
    <div className="col-start-2 row-start-2 flex min-w-0 items-baseline gap-1.5">
      {sprint ? (
        <span
          /* `max-w-[45%]` with `shrink-0`: the chip never yields its space to the text beside it,
             but a pathologically long sprint name still cannot crowd the whole line out. */
          className="max-w-[45%] shrink-0 truncate rounded-sm border border-[color:var(--color-accent)]/35 px-1 text-[11px] leading-[1.35] font-medium text-[color:var(--color-accent)] not-italic"
          title={`Sprint: ${sprint}`}
        >
          {sprint}
        </span>
      ) : null}
      {text ? (
        <p className="min-w-0 truncate text-[11.5px] text-[color:var(--color-ink-subtle)] italic">
          {text}
        </p>
      ) : null}
    </div>
  );
}
